import {
  AlertCircleIcon as HAlertCircle,
  ArrowDown01Icon,
  ArrowDown02Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowTurnBackwardIcon,
  ArrowUp02Icon,
  ArrowUpRight01Icon,
  GoogleIcon as HGoogle,
  GithubIcon as HGithub,
  BubbleChatIcon,
  BubbleChatQuestionIcon,
  Calendar03Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  Coins01Icon,
  Comment01Icon,
  Copy01Icon,
  StopIcon as HStop,
  ComputerIcon,
  ComputerTerminal01Icon,
  CreditCardIcon,
  File01Icon,
  File02Icon,
  Folder01Icon,
  Globe02Icon,
  HelpCircleIcon as HHelpCircle,
  Home01Icon,
  InboxIcon as HInbox,
  Key01Icon,
  Link01Icon,
  ArrowExpand01Icon,
  Logout01Icon,
  Mail01Icon,
  Megaphone01Icon,
  Message01Icon,
  MagicWand01Icon,
  MoreHorizontalIcon,
  Note01Icon,
  Notebook01Icon,
  Notification01Icon,
  PauseIcon as HPause,
  PencilEdit02Icon,
  PlayIcon as HPlay,
  Plug01Icon,
  PlusSignIcon,
  RepeatIcon as HRepeat,
  Search01Icon,
  Settings01Icon,
  SidebarLeft01Icon,
  SidebarRight01Icon,
  SparklesIcon,
  SquareLock02Icon,
  StarsIcon as HStars,
  GridTableIcon,
  TaskDone01Icon,
  Tick02Icon,
  UserAdd01Icon,
  UserGroupIcon,
  UserIcon as HUser,
  UserMultipleIcon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import type { ComponentProps } from "react";

// Hugeicons stroke-rounded, the set Marcel uses for navigation and actions, at a 1.75 stroke
// (his range is 1.5 to 1.75; 1.5 read faint on the dark ground). Icons draw in currentColor,
// so the parent sets the grey; size comes from CSS (size-4, [&_svg]:size-5 and so on).
// Need another one? Pick it from the free stroke-rounded set at hugeicons.com and add a line
// here. Never hand-draw paths.

type IconProps = Omit<ComponentProps<"svg">, "children" | "ref" | "strokeWidth">;

const make = (icon: IconSvgElement, strokeWidth = 1.75) => {
  const Icon = (props: IconProps) => (
    <HugeiconsIcon icon={icon} strokeWidth={strokeWidth} color="currentColor" aria-hidden {...props} />
  );
  return Icon;
};

// Rail and navigation
export const HomeIcon = make(Home01Icon);
export const ChatIcon = make(BubbleChatIcon);
export const TasksIcon = make(TaskDone01Icon);
export const TeamIcon = make(UserGroupIcon);
export const NotebookIcon = make(Notebook01Icon);
export const SettingsIcon = make(Settings01Icon);
export const BellIcon = make(Notification01Icon);
export const SearchIcon = make(Search01Icon);

// Controls
export const ChevronDownIcon = make(ArrowDown01Icon, 2);
export const ChevronRightIcon = make(ArrowRight01Icon, 2);
export const ChevronLeftIcon = make(ArrowLeft01Icon, 2);
export const PlusIcon = make(PlusSignIcon, 2);
export const CloseIcon = make(Cancel01Icon);
export const CheckIcon = make(Tick02Icon, 2.2);
export const ArrowUpIcon = make(ArrowUp02Icon, 2);
export const ArrowDownIcon = make(ArrowDown02Icon, 2);
export const MoreIcon = make(MoreHorizontalIcon, 2.2);
export const ReturnIcon = make(ArrowTurnBackwardIcon);
export const PanelLeftIcon = make(SidebarLeft01Icon);
export const PanelRightIcon = make(SidebarRight01Icon);
export const LinkIcon = make(Link01Icon);
export const ExpandIcon = make(ArrowExpand01Icon);
export const SignOutIcon = make(Logout01Icon);
export const PauseIcon = make(HPause);
export const PlayIcon = make(HPlay);
export const RepeatIcon = make(HRepeat);

// Tools and objects
export const MailIcon = make(Mail01Icon);
export const SheetIcon = make(GridTableIcon);
export const GlobeIcon = make(Globe02Icon);
export const MonitorIcon = make(ComputerIcon);
export const TerminalIcon = make(ComputerTerminal01Icon);
export const ClockIcon = make(Clock01Icon);
export const CalendarIcon = make(Calendar03Icon);
export const LockIcon = make(SquareLock02Icon);
export const KeyIcon = make(Key01Icon);
export const PlugIcon = make(Plug01Icon);
export const CardIcon = make(CreditCardIcon);
export const CoinIcon = make(Coins01Icon);
export const MegaphoneIcon = make(Megaphone01Icon);
export const InboxIcon = make(HInbox);
export const FolderIcon = make(Folder01Icon);
export const FileIcon = make(File01Icon);
export const DocumentIcon = make(File02Icon);
export const PageIcon = make(File02Icon);
export const NoteIcon = make(Note01Icon);
export const EditIcon = make(PencilEdit02Icon);
export const EyeIcon = make(ViewIcon);
export const QuestionIcon = make(BubbleChatQuestionIcon);
export const MessageIcon = make(Message01Icon);
export const SpeechIcon = make(Comment01Icon);
export const SkillIcon = make(MagicWand01Icon);
export const SparkleIcon = make(SparklesIcon);
export const StarsIcon = make(HStars);

// People and states
export const UserIcon = make(HUser);
export const UsersIcon = make(UserMultipleIcon);
export const PeopleIcon = make(UserGroupIcon);
export const UserPlusIcon = make(UserAdd01Icon);
export const DoneIcon = make(CheckmarkCircle02Icon);
export const HelpCircleIcon = make(HHelpCircle);
export const AlertCircleIcon = make(HAlertCircle);

/** The Buddy mark: two eyes on the light tile. */
export const BuddyEyes = (props: IconProps) => (
  <svg viewBox="0 0 18 11" aria-hidden {...props}>
    <ellipse cx="5" cy="5.5" rx="2.1" ry="2.9" fill="currentColor" />
    <ellipse cx="13" cy="5.5" rx="2.1" ry="2.9" fill="currentColor" />
  </svg>
);

// Sign in and setup
export const ExternalIcon = make(ArrowUpRight01Icon);
export const GoogleIcon = make(HGoogle);
export const GithubIcon = make(HGithub);

// Chat
export const CopyIcon = make(Copy01Icon);
export const StopIcon = make(HStop);
